import { useRef, useState } from "react";
import { downloadCsvTemplate } from "../utils/csvTemplates";
import {
  uploadCsvBulk,
  showBulkUploadSummaryToast,
  getBulkUploadErrorMessage,
  FILE_TOO_LARGE_MESSAGE,
  MAX_BULK_UPLOAD_BYTES,
} from "../utils/uploadCsvBulk";
import { useMutationLock } from "../hooks/useMutationLock";

/**
 * Compact CSV bulk-upload controls: file picker + Bulk Upload + Download Template.
 */
export default function BulkCsvControls({
  canEdit = true,
  sampleRows,
  templateFilename,
  endpoint,
  notificationCtx,
  onSuccess,
}) {
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const { busy, run } = useMutationLock();

  if (!canEdit) return null;

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!file) {
      notificationCtx.show({
        message: "Please select a file to upload.",
        stat: "error",
      });
      return;
    }

    if (typeof file.size === "number" && file.size > MAX_BULK_UPLOAD_BYTES) {
      notificationCtx.show({
        message: FILE_TOO_LARGE_MESSAGE,
        stat: "error",
      });
      return;
    }

    await run(async () => {
      try {
        const data = await uploadCsvBulk({
          file,
          endpoint,
        });
        if (data?.success) {
          showBulkUploadSummaryToast(notificationCtx, data.summary);
          onSuccess?.();
          clearFile();
        } else {
          notificationCtx.show({
            message: data?.message || "Bulk upload failed.",
            stat: "error",
          });
        }
      } catch (err) {
        console.error("Bulk upload failed:", err);
        notificationCtx.show({
          message: getBulkUploadErrorMessage(err),
          stat: "error",
        });
      }
    });
  };

  return (
    <div className="d-flex align-items-end" style={{ gap: "14px" }}>
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="form-control"
          style={{
            width: "210px",
            height: "38px",
            backgroundColor: "#fff",
            border: "none",
            borderRadius: "8px",
            fontSize: "12px",
            padding: "7px 10px",
            boxShadow: "0 2px 5px rgba(48,45,69,0.04)",
          }}
        />
      </div>

      <button
        type="button"
        className="btn"
        onClick={handleUpload}
        disabled={!file || busy}
        style={{
          height: "38px",
          backgroundColor: "#f9e1be",
          color: "#62503a",
          border: "none",
          padding: "0 15px",
          borderRadius: "8px",
          fontWeight: "600",
          fontSize: "13px",
          whiteSpace: "nowrap",
        }}
      >
        {busy ? "Uploading..." : "Bulk Upload"}
      </button>

      <button
        type="button"
        className="btn"
        onClick={() => downloadCsvTemplate(templateFilename, sampleRows)}
        style={{
          height: "38px",
          display: "flex",
          alignItems: "center",
          backgroundColor: "#fff",
          color: "#403b55",
          border: "none",
          padding: "0 15px",
          borderRadius: "8px",
          fontWeight: "600",
          fontSize: "13px",
          whiteSpace: "nowrap",
        }}
      >
        Download Template
      </button>
    </div>
  );
}
